import { useOutletContext } from 'react-router-dom'

import { Box, Text } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { CostCenterAssignment } from 'modules/cost_centers/components/CostCenterAssigments/lib/types'

import { Options } from './Options'
import { AssignationsTag, Tag } from './Tag'

interface Options {
  onEdit?: () => void
  onDelete?: () => void
  deleteDisabled?: boolean
}

interface Props {
  assignments: CostCenterAssignment[]
  options?: Options
}

export const AssignmentList = ({ assignments, options }: Props) => {
  const { hasNewStructureEnabled } = useOutletContext<{
    hasNewStructureEnabled?: boolean
  }>()

  const startDate = moment(assignments[0]?.startDate).format('ll')
  const endDate = assignments[0]?.endDate ? moment(assignments[0]?.endDate).format('ll') : null

  const renderLegacyAssignment = (cc: CostCenterAssignment) => (
    <Box key={cc.id} flexDirection="row" justifyContent="spaceBetween" alignItems="center">
      <Text.Normal size="200" color="textSecondary">
        {cc.costCenter?.code
          ? `${cc.costCenter.code} - ${cc.costCenter.name}`
          : cc.costCenter?.name}
      </Text.Normal>

      <Tag text={`${(cc.percentage * 100).toFixed(2)}%`} background="grey400" />
    </Box>
  )

  const renderStructuredAssignment = (cc: CostCenterAssignment) => {
    const costCenter = cc.costCenter
    const dimensions = costCenter?.dimensions ?? []

    // Build a display name from dimensions (same pattern as formatter.ts)
    const dimensionParts: string[] = []
    for (const dimension of dimensions) {
      if (dimension.jobCatalogFamilyName) dimensionParts.push(dimension.jobCatalogFamilyName)
      if (dimension.jobCatalogFunctionName) dimensionParts.push(dimension.jobCatalogFunctionName)
      if (dimension.jobCatalogRoleName) dimensionParts.push(dimension.jobCatalogRoleName)
      if (dimension.workplaceName) dimensionParts.push(dimension.workplaceName)
    }

    const dimensionName =
      dimensionParts.length > 0 ? dimensionParts.join(' - ') : (costCenter?.name ?? '')
    const displayName = costCenter?.code ? `${costCenter.code} - ${dimensionName}` : dimensionName

    // If no structured data, fall back to legacy display
    if (!displayName) {
      return renderLegacyAssignment(cc)
    }

    return (
      <Box key={cc.id} flexDirection="row" justifyContent="spaceBetween" alignItems="center">
        <Text.Normal size="200" color="textSecondary">
          {displayName}
        </Text.Normal>

        <Tag text={`${(cc.percentage * 100).toFixed(2)}%`} background="grey400" />
      </Box>
    )
  }

  return (
    <>
      <Box flexDirection="row" alignItems="flexStart" justifyContent="spaceBetween">
        <Box flexDirection="row" gap="s8" paddingBottom="s20">
          <Text.Normal weight="medium" size="300" color="grey1000">
            {startDate} {endDate ? ` - ${endDate}` : ''}
          </Text.Normal>

          <AssignationsTag
            startDate={String(assignments[0]?.startDate)}
            endDate={assignments[0]?.endDate}
          />
        </Box>

        {options && (
          <Options
            onEdit={options.onEdit}
            onDelete={options.onDelete}
            deleteDisabled={options.deleteDisabled}
          />
        )}
      </Box>

      <Box gap="s6">
        {assignments.map((cc) =>
          hasNewStructureEnabled ? renderStructuredAssignment(cc) : renderLegacyAssignment(cc)
        )}
      </Box>
    </>
  )
}
